/**
 * The host's Creative Library picker (ADR-037 D8) — opened when an arche calls
 * the `creative-picker` client-side entity (`useCreativeLibrary().pick()`).
 * The person picks one of the styles or characters the arche can use, seeing
 * its pictures, or closes the sheet.
 *
 * The list is the arche's own: `library` makes the arche's `list_items` and
 * `get_item` calls with its bridge token (PluginBridge), so the sheet shows
 * exactly what the arche's access decision lets it read — its Personal
 * library, or a workspace's that approved it — and never more. Every word is
 * the platform's apart from the library's own names and words. The arche
 * learns only the item picked.
 */

import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CreativeStudioLink } from '@/components/CreativeStudioLink';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { CreativeItemSummary } from '@/lib/api';
import type { CreativePickerKind, CreativePickerResult } from '@fias/client-entities';

/** The arche's own library calls, made by the host with its bridge token. */
export interface CreativePickerLibrary {
  list(kind: CreativePickerKind | null): Promise<CreativeItemSummary[]>;
  get(itemId: string): Promise<CreativeItemSummary>;
}

interface CreativePickerSheetProps {
  open: boolean;
  archeName: string;
  kind: CreativePickerKind | null;
  library: CreativePickerLibrary;
  onResolve: (result: CreativePickerResult) => void;
}

const NOUN: Record<'any' | CreativePickerKind, string> = {
  any: 'style or character',
  style: 'style',
  character: 'character',
};

export function CreativePickerSheet({
  open,
  archeName,
  kind,
  library,
  onResolve,
}: CreativePickerSheetProps) {
  const noun = NOUN[kind ?? 'any'];
  // Read fresh for each sheet, never cached: it is the arche's view, and the
  // next sheet may be another arche's.
  const [items, setItems] = useState<{
    data?: CreativeItemSummary[];
    isLoading: boolean;
    isError: boolean;
  }>({ isLoading: true, isError: false });
  useEffect(() => {
    let live = true;
    library.list(kind).then(
      (data) => live && setItems({ data, isLoading: false, isError: false }),
      () => live && setItems({ isLoading: false, isError: true }),
    );
    return () => {
      live = false;
    };
  }, [library, kind]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedId && items.data?.length) setSelectedId(items.data[0].itemId);
  }, [items.data, selectedId]);
  const [pictures, setPictures] = useState<{
    itemId: string;
    previews: NonNullable<CreativeItemSummary['previews']>;
  } | null>(null);
  useEffect(() => {
    if (!selectedId) return;
    let live = true;
    library.get(selectedId).then(
      (item) => live && setPictures({ itemId: selectedId, previews: item.previews ?? [] }),
      () => live && setPictures({ itemId: selectedId, previews: [] }),
    );
    return () => {
      live = false;
    };
  }, [library, selectedId]);
  const previews = pictures?.itemId === selectedId ? pictures.previews : null;
  const chosen = items.data?.find((i) => i.itemId === selectedId) ?? null;

  function use() {
    if (!chosen) return;
    onResolve({
      item: {
        itemId: chosen.itemId,
        kind: chosen.kind,
        name: chosen.name,
        version: chosen.version,
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onResolve({ canceled: true })}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>
            Pick a {noun} for {archeName}
          </DialogTitle>
          <DialogDescription>
            Shown by Fias, from your Creative Library. {archeName} gets only the one you pick.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {items.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : items.isError ? (
            <p className="text-sm text-destructive" role="alert">
              Your Creative Library could not be loaded here.
            </p>
          ) : (items.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No {kind ? `${noun}s` : 'styles or characters'} here yet.{' '}
              <CreativeStudioLink>
                {(studio) => `Make one in ${studio}, then pick again`}
              </CreativeStudioLink>
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-[14rem_1fr]">
              <ul
                className="max-h-80 space-y-0.5 overflow-y-auto"
                role="listbox"
                aria-label="Your Creative Library"
              >
                {(items.data ?? []).map((item) => (
                  <li key={item.itemId}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={item.itemId === selectedId}
                      className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted aria-selected:bg-muted"
                      onClick={() => setSelectedId(item.itemId)}
                    >
                      <span className="block truncate">{item.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {kind ? item.words || ' ' : item.kind}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="min-w-0 space-y-2" aria-live="polite">
                {chosen && (
                  <>
                    <p className="text-sm font-medium">{chosen.name}</p>
                    {chosen.words && (
                      <p className="line-clamp-4 text-xs text-muted-foreground">{chosen.words}</p>
                    )}
                    {previews === null ? (
                      <p className="text-xs text-muted-foreground">Loading pictures…</p>
                    ) : previews.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {previews.slice(0, 6).map((p) => (
                          <img
                            key={p.documentId}
                            src={p.url}
                            alt=""
                            className="aspect-square w-full rounded-md border object-cover"
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No pictures.</p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onResolve({ canceled: true })}>
            Cancel
          </Button>
          <Button onClick={use} disabled={!chosen}>
            <Check className="mr-1 h-4 w-4" aria-hidden="true" />
            Use {chosen ? `“${chosen.name}”` : `this ${noun}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
